import { __decorate } from "tslib";
import { consume } from '@lit/context';
import { html } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { componentStyles } from '../../shared/styles/component.styles.js';
import { formControlStyles } from '../../shared/styles/form-control.styles.js';
import HelixElement from '../../shared/types/helix-element.js';
import { radioGroupAutocompleteContext, radioGroupContext, radioGroupDisabledContext, radioGroupNameContext, radioGroupReadonlyContext, radioGroupValueContext, } from './radio-group-context.js';
import { radioGroupOptionStyles } from './radio-group-option.styles.js';
import { SlotController } from '../../shared/controllers/slot.controller.js';
let HlxRadioGroupOption = class HlxRadioGroupOption extends HelixElement {
    constructor() {
        super(...arguments);
        this.slotController = new SlotController(this, 'label', 'description', 'appendix');
        this.tabIndex = 0;
        this.role = 'radio';
        this.inputId = crypto.randomUUID();
    }
    render() {
        const disabled = this.disabled !== undefined || this.disabledByGroup;
        const readonly = this.readonly !== undefined || this.readonlyByGroup;
        const showLabel = this.label || this.slotController.hasSlot('label');
        const showDescription = this.description || this.slotController.hasSlot('description');
        // this cannot be called from attributeChanged, bc disabledByGroup & readonlyByGroup
        // are not reflected in attribute changes
        this.updateAriaLabels();
        return html `
            <span
                role="presentation"
                class="${classMap({
            'hlx-radio-option': true,
            'hlx-radio-option-disabled': disabled,
            'hlx-radio-option-read-only': readonly,
            'hlx-radio-option-show-label': showLabel,
            'hlx-radio-option-show-description': showDescription,
            'hlx-radio-option-show-appendix': this.slotController.hasSlot('appendix'),
        })}">
                <input
                    aria-hidden="true"
                    tabindex=${this.tabIndex}
                    id=${this.inputId}
                    part="input"
                    type="radio"
                    class="hlx-radio-option-control"
                    name=${ifDefined(this.name)}
                    .value=${this.value}
                    .checked=${this.selectedValue === this.value}
                    ?disabled=${disabled}
                    ?readonly=${readonly}
                    autocomplete=${ifDefined(this.autocomplete)}
                    @click=${this.handleClick}
                    @change=${this.select}
                    @focus=${this.handleFocus}
                    @blur=${this.handleBlur} />
                <label
                    for=${this.inputId}
                    class="hlx-radio-option-legend"
                    part="hlx-radio-option-legend">
                    <slot class="hlx-radio-option-label">${this.label}</slot>
                    <slot class="hlx-radio-option-description" name="description">
                        ${this.description}
                    </slot>
                </label>
            </span>
            <slot name="appendix" class="hlx-appendix-box"></slot>
        `;
    }
    connectedCallback() {
        super.connectedCallback();
        this.context?.add(this);
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.context?.remove(this);
    }
    updateAriaLabels() {
        if (this.disabled !== undefined || this.disabledByGroup) {
            this.setAttribute('aria-disabled', 'true');
        }
        else {
            this.removeAttribute('aria-disabled');
        }
        if (this.readonly !== undefined || this.readonlyByGroup) {
            this.setAttribute('aria-readonly', 'true');
        }
        else {
            this.removeAttribute('aria-readonly');
        }
        if (this.value === this.selectedValue) {
            this.setAttribute('aria-checked', 'true');
        }
        else {
            this.setAttribute('aria-checked', 'false');
        }
    }
    focus(options) {
        this.input.focus(options);
    }
    select() {
        this.context?.select(this);
        this.emit('hlx-change');
    }
    handleBlur() {
        this.emit('hlx-blur');
    }
    handleFocus() {
        this.emit('hlx-focus');
    }
    handleClick(event) {
        // unfortunately, setting `readonly` on radio-button doesn't affect it's
        // behavior by itself @see https://stackoverflow.com/q/1953017/1105860
        const readonly = this.readonly || this.readonlyByGroup;
        if (readonly) {
            event.preventDefault();
            event.stopImmediatePropagation();
            return;
        }
    }
};
HlxRadioGroupOption.styles = [componentStyles, formControlStyles, radioGroupOptionStyles];
__decorate([
    consume({ context: radioGroupContext })
], HlxRadioGroupOption.prototype, "context", void 0);
__decorate([
    consume({ context: radioGroupValueContext, subscribe: true })
], HlxRadioGroupOption.prototype, "selectedValue", void 0);
__decorate([
    query('input')
], HlxRadioGroupOption.prototype, "input", void 0);
__decorate([
    consume({ context: radioGroupNameContext, subscribe: true }),
    property({ type: String, reflect: true, attribute: true })
], HlxRadioGroupOption.prototype, "name", void 0);
__decorate([
    consume({ context: radioGroupAutocompleteContext, subscribe: true }),
    property({ type: String })
], HlxRadioGroupOption.prototype, "autocomplete", void 0);
__decorate([
    property({ type: String })
], HlxRadioGroupOption.prototype, "label", void 0);
__decorate([
    property({ type: String })
], HlxRadioGroupOption.prototype, "description", void 0);
__decorate([
    property({ type: String, reflect: true, attribute: true })
], HlxRadioGroupOption.prototype, "value", void 0);
__decorate([
    consume({ context: radioGroupDisabledContext, subscribe: true })
], HlxRadioGroupOption.prototype, "disabledByGroup", void 0);
__decorate([
    property({ type: Boolean, reflect: true, attribute: true })
], HlxRadioGroupOption.prototype, "disabled", void 0);
__decorate([
    consume({ context: radioGroupReadonlyContext, subscribe: true })
], HlxRadioGroupOption.prototype, "readonlyByGroup", void 0);
__decorate([
    property({ type: Boolean, reflect: true, attribute: true })
], HlxRadioGroupOption.prototype, "readonly", void 0);
__decorate([
    property({ type: Number, attribute: 'tab-index' })
], HlxRadioGroupOption.prototype, "tabIndex", void 0);
__decorate([
    property({ type: String, reflect: true })
], HlxRadioGroupOption.prototype, "role", void 0);
HlxRadioGroupOption = __decorate([
    customElement('hlx-radio-group-option')
], HlxRadioGroupOption);
export { HlxRadioGroupOption };
//# sourceMappingURL=radio-group-option.js.map